import React, { useEffect, useRef, useState } from 'react';

/**
 * PortalVenuePicker — real POI search (restaurants, bars, parks, clubs,
 * attractions) biased to a given city's coords.
 *   - Uses the Mapbox Search Box API `/forward` endpoint, which has far
 *     stronger POI coverage than the legacy v5 geocoder. `proximity` locks
 *     results to the primary city, and a distance filter drops anything that
 *     leaked in from another city.
 *   - Shows the currently-selected venue as a tile with a CHANGE button.
 *
 * Venue is optional. The user can skip it and the Signal will still publish
 * at the city centroid.
 */
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Keep results in the city the host is looking at — drop POIs further than this
// from the city centre (covers a metro area without bleeding into the next city).
const MAX_KM_FROM_CITY = 80;

function distanceKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Mapbox poi_category like "restaurant", "bar", "park" → a clean display label.
function prettyCategory(cat) {
  if (!cat) return '';
  return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function PortalVenuePicker({
  value, // { name, address, lat, lng } | null
  onChange,
  cityCoords, // { lat, lng } — proximity bias
  cityLabel,
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q || !MAPBOX_TOKEN) { setResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          q,
          limit: '10',
          language: 'en',
          // POIs first (restaurants, bars, clubs, parks, attractions), with
          // addresses + places as a fallback for "meet at 4th & Main" cases.
          types: 'poi,address,place',
          access_token: MAPBOX_TOKEN,
        });
        if (cityCoords?.lng != null && cityCoords?.lat != null) {
          params.set('proximity', `${cityCoords.lng},${cityCoords.lat}`);
        }
        const url = `https://api.mapbox.com/search/searchbox/v1/forward?${params.toString()}`;
        const r = await fetch(url);
        const data = await r.json();
        let items = (data.features || []).map((f) => {
          const [lng, lat] = f.geometry?.coordinates || [];
          const p = f.properties || {};
          const name = p.name || p.name_preferred || '';
          const address = p.full_address || p.address || p.place_formatted || '';
          const category = Array.isArray(p.poi_category) ? p.poi_category[0] : null;
          return { name, address, lat, lng, category };
        }).filter((it) => it.name && typeof it.lat === 'number');

        // Keep results in the city the host is searching.
        if (cityCoords?.lat != null && cityCoords?.lng != null) {
          items = items.filter((it) => distanceKm(cityCoords.lat, cityCoords.lng, it.lat, it.lng) <= MAX_KM_FROM_CITY);
        }
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, cityCoords?.lat, cityCoords?.lng]);

  const pick = (v) => {
    onChange?.(v);
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  if (value) {
    return (
      <div className="portal-card" style={{ padding: 14, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="portal-serif" style={{ fontSize: 18, color: '#ECE7DA', marginBottom: 4 }}>
            <em style={{ fontStyle: 'italic' }}>@</em> {value.name}
          </p>
          {value.address && (
            <p className="portal-whisper" style={{ marginTop: 2 }}>{value.address}</p>
          )}
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => onChange?.(null)}
            className="portal-mono"
            style={{ background: 'none', border: 'none', color: '#E4C77E', cursor: 'pointer', fontSize: 10, letterSpacing: '0.1em' }}
          >
            CHANGE
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        className="portal-input"
        placeholder={cityLabel ? `Search a place in ${cityLabel}…` : 'Search a place…'}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        disabled={disabled || !cityCoords}
      />
      {!cityCoords && (
        <p className="portal-whisper" style={{ marginTop: 6 }}>Pick a city first to search venues nearby.</p>
      )}
      {open && cityCoords && (results.length > 0 || searching || query.trim()) && (
        <div className="portal-card" style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          padding: 0, maxHeight: 280, overflowY: 'auto', zIndex: 5,
        }}>
          {searching && results.length === 0 ? (
            <p className="portal-mono" style={{ padding: 12, fontSize: 11, color: 'rgba(236,231,218,0.34)' }}>SEARCHING…</p>
          ) : results.length === 0 ? (
            <p className="portal-mono" style={{ padding: 12, fontSize: 11, color: 'rgba(236,231,218,0.34)' }}>NO RESULTS</p>
          ) : (
            results.map((r, i) => (
              <button
                key={`${r.name}-${i}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(r)}
                style={{
                  display: 'block', width: '100%', padding: '12px 14px', textAlign: 'left',
                  background: 'transparent', border: 'none', borderBottom: '1px solid rgba(236,231,218,0.12)',
                  color: '#ECE7DA', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div className="portal-serif" style={{ fontSize: 15, marginBottom: 2 }}>{r.name}</div>
                  {r.category && (
                    <span className="portal-mono" style={{ fontSize: 9, letterSpacing: '0.12em', color: '#C9A24B', textTransform: 'uppercase', flexShrink: 0 }}>
                      {prettyCategory(r.category)}
                    </span>
                  )}
                </div>
                {r.address && <div className="portal-whisper" style={{ fontSize: 11 }}>{r.address}</div>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
